'use client'

import { Badge } from '@vinted/web-ui'
import { MouseEvent } from 'react'
import { useIntl } from 'react-intl'

import { ItemBoxPriceBreakdown } from '@marketplace-web/escrow-pricing/escrow-pricing-feature'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { transformAbsoluteUrlToRelative } from '@marketplace-web/browser/url-util'
import { ProductItemModel, getProductItemStatus } from '@marketplace-web/item-box/product-item-data'
import { getItemStatusMessage } from '@marketplace-web/item-page/item-status-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'

import SellerItemBox from '../SellerItemBox'

type Props = {
  item: ProductItemModel
  renderFooter?: JSX.Element | null
  showStatus?: boolean
  showStatusAsBadge?: boolean
  testId?: string
  url?: string
  isUrlDisabled?: boolean
  showPriceBreakdown?: boolean
  hideBusinessBadge?: boolean
  isBumpTextHidden?: boolean
  showPhotoCollage?: boolean
  imagesExperimentalOverlayDisabled?: boolean
  onClick?: (event: MouseEvent) => void
}

const SellerProductItem = ({
  item,
  onClick,
  renderFooter,
  testId,
  url,
  showStatus = false,
  showStatusAsBadge = false,
  isUrlDisabled = false,
  showPriceBreakdown = true,
  hideBusinessBadge,
  showPhotoCollage,
  imagesExperimentalOverlayDisabled,
  isBumpTextHidden = false,
}: Props) => {
  const translate = useTranslate()
  const { user: sessionUser } = useSession()
  const { locale } = useIntl()

  const isItemUrlOriginMapperEnabled = useFeatureSwitch('item_url_origin_mapper')

  const viewedByGod = sessionUser?.is_god ?? false
  const viewedByModerator = sessionUser?.moderator ?? false

  const isPrivilegedUser = sessionUser?.id === item.user.id || viewedByGod || viewedByModerator

  const getTranslatedStatusMessage = () => {
    const status = showStatus ? getProductItemStatus(item, isPrivilegedUser) : null

    const statusMessage = getItemStatusMessage(status)

    if (!statusMessage) return null

    return {
      content: translate(statusMessage.content),
      theme: statusMessage.theme,
    }
  }

  const getBadge = (): React.ComponentProps<typeof Badge> | null => {
    if (showStatusAsBadge) {
      const status = getTranslatedStatusMessage()

      if (status) {
        return status
      }
    }

    if (!item.badge) return null

    const { theme, body } = item.badge

    return { theme, content: body }
  }

  function getSecondaryBadge() {
    if (!item.user.isBusiness || hideBusinessBadge) return undefined

    return {
      content: translate('business.badge'),
    }
  }

  const getStatusProps = (): React.ComponentProps<typeof SellerItemBox>['status'] | null => {
    if (showStatusAsBadge) return null

    return getTranslatedStatusMessage()
  }

  const { id, priceWithDiscount, price, thumbnailUrl, thumbnailUrls, dominantColor, itemBox } = item

  let itemUrl = url || item.url
  itemUrl = isItemUrlOriginMapperEnabled ? transformAbsoluteUrlToRelative(itemUrl) : itemUrl

  const currentPrice = formatCurrencyAmount(priceWithDiscount || price, locale)
  const oldPrice = priceWithDiscount ? formatCurrencyAmount(price, locale) : null

  const itemTestId = testId || `product-item-id-${id}`

  const getBumpText = () => {
    const isPromotedStatusHidden = !getStatusProps() && item.isPromoted

    if (!isPromotedStatusHidden) return null

    return <span data-testid="bump-badge">{translate('item.status.bumped')}</span>
  }

  return (
    <SellerItemBox
      url={isUrlDisabled ? undefined : itemUrl}
      price={currentPrice}
      oldPrice={oldPrice}
      image={thumbnailUrl}
      imageColor={dominantColor}
      imagesExperimental={showPhotoCollage ? thumbnailUrls : undefined}
      onClick={onClick}
      renderFooter={renderFooter}
      status={getStatusProps()}
      badge={getBadge()}
      secondaryBadgeExperimental={getSecondaryBadge()}
      bumpText={!isBumpTextHidden && getBumpText()}
      testId={itemTestId}
      renderPriceBreakdown={
        showPriceBreakdown && (
          <ItemBoxPriceBreakdown
            testId={itemTestId}
            serviceFee={item.serviceFee}
            id={item.id}
            price={item.price}
            totalItemPrice={item.totalItemPrice}
            user={item.user}
            priceWithDiscount={item.priceWithDiscount}
            itemTitle={item.title}
            itemPhotoSrc={item.thumbnailUrl}
          />
        )
      }
      imagesExperimentalOverlayDisabled={imagesExperimentalOverlayDisabled}
      itemBox={itemBox}
    />
  )
}

export default SellerProductItem
